import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { REFERRER_TYPE, PAGE_TYPE } from '../../constants/gaEnums';
import { trackMenuClick } from '../../lib/gaEvents';
import { getPageTypeFromPath } from '../../lib/gaParams';
import { MdMenu } from 'react-icons/md';
import { FaInstagram, FaYoutube } from 'react-icons/fa';
import '../../styles/HeaderMobile.css';
import logo from '../../assets/logo_black.png';
import logoWhite from '../../assets/logo_white.png';
import { useUserStore } from '../../stores/userStore';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SearchIcon from '@mui/icons-material/Search';
import MobileSearchBar from '../common/MobileSearchbar';
import HeaderProfileButton from '../common/HeaderProfileButton';
import ColorSchemeButton from '../common/ColorSchemeButton';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const SIDEBAR_CLOSE_FALLBACK_MS = 220;

const HeaderMobile = () => {
  const isAdmin = useUserStore((state) => state.isAdmin);
  const user = useUserStore((state) => state.user);
  const location = useLocation();
  const currentPageType = getPageTypeFromPath(location.pathname);
  const { resolvedMode } = useColorScheme();
  const [showSidebar, setShowSidebar] = useState(false);
  const [isSidebarMounted, setIsSidebarMounted] = useState(false);
  const [isSidebarClosing, setIsSidebarClosing] = useState(false);
  const [autoFocusSearch, setAutoFocusSearch] = useState(false);
  const proxyInputRef = useRef(null);
  const sidebarCloseTimerRef = useRef(null);
  const sidebarTouchStartYRef = useRef(0);
  const [isLandscape, setIsLandscape] = useState(window.innerWidth > window.innerHeight);

  // 가로/세로 모드 감지
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (sidebarCloseTimerRef.current) {
        clearTimeout(sidebarCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const restoreScrollLock = () => {
      const sidebar = document.querySelector('.mobile-sidebar');
      if (sidebar) {
        sidebar.style.overflowY = '';
      }
    };

    if (showSidebar) {
      const sidebar = document.querySelector('.mobile-sidebar');
      if (sidebar) {
        sidebar.style.overflowY = isLandscape ? 'auto' : 'hidden';
      }

      return () => {
        restoreScrollLock();
      };
    }

    restoreScrollLock();

    return restoreScrollLock;
  }, [showSidebar, isLandscape]);

  useEffect(() => {
    if (!showSidebar) {
      return undefined;
    }

    const getSidebar = () => document.querySelector('.mobile-sidebar');

    const canScrollSidebar = (target, deltaY) => {
      const sidebar = getSidebar();
      if (!sidebar || !target || !(target instanceof Node) || !sidebar.contains(target)) {
        return false;
      }

      if (sidebar.scrollHeight <= sidebar.clientHeight) {
        return false;
      }

      const atTop = sidebar.scrollTop <= 0;
      const atBottom = sidebar.scrollTop + sidebar.clientHeight >= sidebar.scrollHeight - 1;

      if (deltaY > 0 && atTop) {
        return false;
      }

      if (deltaY < 0 && atBottom) {
        return false;
      }

      return true;
    };

    const handleTouchStart = (event) => {
      sidebarTouchStartYRef.current = event.touches[0]?.clientY || 0;
    };

    const handleTouchMove = (event) => {
      const currentY = event.touches[0]?.clientY || 0;
      const deltaY = currentY - sidebarTouchStartYRef.current;

      if (!canScrollSidebar(event.target, deltaY)) {
        event.preventDefault();
      }
      sidebarTouchStartYRef.current = currentY;
    };

    const handleWheel = (event) => {
      if (!canScrollSidebar(event.target, -event.deltaY)) {
        event.preventDefault();
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('wheel', handleWheel);
    };
  }, [showSidebar]);

  const openSidebar = (focusSearch = false) => {
    if (sidebarCloseTimerRef.current) {
      clearTimeout(sidebarCloseTimerRef.current);
      sidebarCloseTimerRef.current = null;
    }

    setIsSidebarClosing(false);
    if (focusSearch) {
      // iOS Safari: 클릭 핸들러 내에서 동기적으로 focus해야 키보드가 뜸
      proxyInputRef.current?.focus();
    }

    setAutoFocusSearch(focusSearch);
    setIsSidebarMounted(true);
    window.requestAnimationFrame(() => {
      setShowSidebar(true);
    });
  };

  const closeSidebar = () => {
    setAutoFocusSearch(false);
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    setIsSidebarClosing(true);
    setShowSidebar(false);
    sidebarCloseTimerRef.current = window.setTimeout(() => {
      setIsSidebarMounted(false);
      setIsSidebarClosing(false);
      sidebarCloseTimerRef.current = null;
    }, SIDEBAR_CLOSE_FALLBACK_MS);
  };

  const handleSidebarTransitionEnd = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }

    if (!showSidebar) {
      if (sidebarCloseTimerRef.current) {
        clearTimeout(sidebarCloseTimerRef.current);
        sidebarCloseTimerRef.current = null;
      }
      setIsSidebarMounted(false);
      setIsSidebarClosing(false);
    }
  };

  const handleNavClick = () => {
    if (showSidebar) {
      closeSidebar();
    }
  };

  const handleExternalLinkClick = (url) => {
    closeSidebar();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // TODO: 리뉴얼 후 Shop 다시 활성화
  // const handleExternalLinkClick = () => {
  //   if (showSearch) {
  //     setShowSearch(false);
  //   }
  //   if (showSidebar) {
  //     setShowSidebar(false);
  //   }
  // };

  const toggleSidebar = () => {
    if (showSidebar || isSidebarMounted) {
      closeSidebar();
      return;
    }

    openSidebar();
  };

  const openSidebarWithSearch = () => {
    openSidebar(true);
  };

  const sidebarClassName = [
    'mobile-sidebar',
    showSidebar ? 'open' : '',
    isSidebarClosing ? 'closing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header className="header-mobile">
        <div className="header-mobile-inner">
          <div className="logo-mobile">
            <Link
              to="/"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => {
                handleNavClick('/');
                window.scrollTo({ top: 0, behavior: 'auto' });
              }}
            >
              <img
                src={resolvedMode === 'dark' ? logoWhite : logo}
                alt="logo"
                className="logo-mobile-img"
              />
            </Link>
          </div>

          <div className="topbar-icons-mobile">
            <button className="icon-btn-mobile" title="검색" onClick={openSidebarWithSearch}>
              <SearchIcon sx={{ fontSize: 24 }} />
            </button>
            <ColorSchemeButton className="icon-btn-mobile" iconSize={24} />
            {isAdmin && (
              <Link
                to="/admin"
                className="icon-btn-mobile"
                title="관리자 페이지"
                onClick={() => handleNavClick('/admin')}
              >
                <ManageAccountsIcon sx={{ fontSize: 24 }} />
              </Link>
            )}
            <HeaderProfileButton variant="mobile" />
            <button className="icon-btn-mobile" title="메뉴" onClick={toggleSidebar}>
              <MdMenu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* 모바일 사이드바 */}
      {isSidebarMounted && (
        <div className={sidebarClassName} onTransitionEnd={handleSidebarTransitionEnd}>
          <div className="sidebar-header">
            <MobileSearchBar
              onClose={closeSidebar}
              autoFocus={autoFocusSearch}
              pageType={currentPageType}
            />
          </div>
          <nav className="sidebar-nav">
            <Link
              to="/albums"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => {
                handleNavClick('/albums');
                trackMenuClick(
                  { targetPageType: PAGE_TYPE.REVIEWS_MAIN, pageType: currentPageType },
                  user,
                );
              }}
            >
              앨범
            </Link>
            <Link
              to="/reviews"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => {
                handleNavClick('/reviews');
                trackMenuClick(
                  { targetPageType: PAGE_TYPE.REVIEWS_MAIN, pageType: currentPageType },
                  user,
                );
              }}
            >
              리뷰
            </Link>
            <Link
              to="/lists"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => {
                handleNavClick('/lists');
                trackMenuClick(
                  { targetPageType: PAGE_TYPE.LISTS_MAIN, pageType: currentPageType },
                  user,
                );
              }}
            >
              플리
            </Link>
            <Link
              to="/feed"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => handleNavClick('/feed')}
            >
              피드
            </Link>
            <Link
              to="/notice"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => {
                handleNavClick('/notice');
                trackMenuClick(
                  { targetPageType: PAGE_TYPE.NOTICE_MAIN, pageType: currentPageType },
                  user,
                );
              }}
            >
              공지
            </Link>
            {/* TODO: 리뉴얼 후 Shop 다시 활성화
          <a
            href="https://mkt.shopping.naver.com/link/6821af9662fffc0a4a4a5a56"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleExternalLinkClick}
          >
            Shop
          </a>
          */}
            <Link
              to="/contact"
              state={{ referrerType: REFERRER_TYPE.MENU }}
              onClick={() => {
                handleNavClick('/contact');
                trackMenuClick({ targetPageType: 'contact', pageType: currentPageType }, user);
              }}
            >
              문의
            </Link>
            <div className="sidebar-social-links">
              <div className="sidebar-social-icons">
                <button
                  type="button"
                  aria-label="Instagram"
                  onClick={() => handleExternalLinkClick('https://www.instagram.com/postpop.kr')}
                >
                  <FaInstagram size={18} />
                </button>
                <button
                  type="button"
                  aria-label="Youtube"
                  onClick={() => handleExternalLinkClick('https://www.youtube.com/@포스트팝')}
                >
                  <FaYoutube size={19} />
                </button>
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* iOS 키보드 활성화용 프록시 input */}
      <input
        ref={proxyInputRef}
        style={{ position: 'fixed', opacity: 0, top: -100, left: -100, width: 0, height: 0 }}
        readOnly
      />

      {/* 사이드바 오버레이 */}
      {showSidebar && <div className="sidebar-overlay" onClick={closeSidebar} />}
    </>
  );
};

export default HeaderMobile;
